'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * A mounted application, filling the page under the tenant's header.
 *
 * <p>The height cannot be written in CSS, because what is above the frame is
 * the operator's chrome: a header that may be one row or two, sticky or not,
 * with or without the trade VAT strip under it. So the frame measures where it
 * starts and takes the rest of the viewport, and re-measures when the window
 * resizes or the chrome changes height. `100dvh` in the fallback keeps a
 * usable frame before hydration and on a phone, whose toolbars make `vh` lie.
 *
 * <p>While a frame is on the page the document does not scroll: the app owns
 * the wheel, and a shop footer half a screen below a full-height canvas is not
 * reachable in any case. The rule is written with `:has`, so it applies from
 * the first paint rather than after hydration.
 *
 * <p>A framed app can ask, by message, to go immersive — hiding the shop chrome
 * and covering the viewport — because some of its own pages (a design open in
 * the kitchen designer) need every pixel. The list pages ask for the chrome
 * back. The app opened at such a page directly starts immersive, so the header
 * never flashes in before the app can ask.
 */
const FRAME_CSS = `
html:has(.storefront-app-frame), body:has(.storefront-app-frame){height:100%;overflow:hidden;overscroll-behavior:none}
body:has(.storefront-app-frame) footer{display:none}
.storefront-app-frame{width:100%;height:calc(100dvh - 8rem);min-height:24rem;background:#fff}
.storefront-app-frame > iframe{display:block;width:100%;height:100%;border:0}
html.storefront-app-immersive header{display:none}
html.storefront-app-immersive .storefront-app-frame{position:fixed;inset:0;height:100dvh;min-height:0;z-index:40}
`;

/** The message a framed app sends to hide (`hidden`) or restore (`shown`) the shop chrome. */
const CHROME_MESSAGE = 'storefront-app:chrome';

/** A design opened directly (…?project=…) starts immersive so its header never flashes in. */
function immersiveSource(src: string): boolean {
  return /[?&]project=/.test(src);
}

export function StaticAppFrame({ src, title }: { src: string; title: string }) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [immersive, setImmersive] = useState(() => immersiveSource(src));

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const root = document.documentElement;
    root.classList.toggle('storefront-app-immersive', immersive);
    const fit = () => {
      if (immersive) {
        element.style.height = ''; // the immersive rule covers the viewport
        return;
      }
      const top = element.getBoundingClientRect().top;
      element.style.height = `${Math.max(320, Math.round(window.innerHeight - Math.max(0, top)))}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    // The chrome above can change height on its own: a search panel opening, a
    // menu wrapping to a second row, the trade VAT strip appearing once the
    // shopper is known.
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => fit());
    const header = document.querySelector('header');
    if (observer && header) observer.observe(header);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frame.current?.contentWindow) return;
      const data = event.data as { type?: unknown; chrome?: unknown } | null;
      if (!data || data.type !== CHROME_MESSAGE) return;
      setImmersive(data.chrome === 'hidden');
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', fit);
      window.removeEventListener('message', onMessage);
      observer?.disconnect();
      root.classList.remove('storefront-app-immersive');
    };
  }, [immersive]);

  return (
    <div ref={box} className="storefront-app-frame">
      <style>{FRAME_CSS}</style>
      <iframe
        ref={frame}
        src={src}
        title={title}
        // The app is same-origin, so nothing here grants it anything it would
        // not have as a page of its own; these are the capabilities a browser
        // withholds from a frame by default and a design tool needs.
        allow="clipboard-write; fullscreen"
      />
    </div>
  );
}
